import { memo, useCallback, useState, useEffect, useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import axios from 'axios';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';

import CapturedImage from '#client/components/CheckDeposit/components/CapturedImage';
import DepositMoneyInput from '#client/components/CheckDeposit/components/DepositMoneyInput';
import DepositTo from '#client/components/CheckDeposit/components/DepositTo';
import {
  CAPTURE_BACK_SIDE,
  CAPTURE_FRONT_SIDE
} from '#client/components/CheckDeposit/constants/captureSide';
import { CHECK_DEPOSIT_FAILED_ERROR_MESSAGE } from '#client/components/CheckDeposit/constants/messages';
import { CHECK_DEPOSIT_UNIT_STATUS } from '#client/components/CheckDeposit/constants/status';
import {
  createFormData,
  fetchAndSaveImage
} from '#client/components/CheckDeposit/utils/imageHandler';
import CheckDepositHistory from '#client/components/CheckDepositHistory';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import Switch from '#client/components/Switch';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { CHECK_DEPOSIT_STATUS } from '#client/constants/transaction';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckDepositLimits from '#client/hooks/useCheckDepositLimits';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useFormStep from '#client/hooks/useFormStep';
import useMitekSdkContext from '#client/hooks/useMitekSdk';
import { CHECK_FRONT_DOC_TYPE } from '#client/hooks/useMitekSdk/constants';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEFAULT_SEGMENT } from '#client/manifest';
import { dispatchRefetchCheckDeposit } from '#client/utils/event';
import { checkImageDataToRotate } from '#client/utils/images';

import {
  DialogContentStyled,
  DividerStyled,
  SectionWrapperStyled,
  Wrapper
} from '../ResubmitModal.styles';

import { RESUBMIT_ACTIONS } from '../constants/actions';
import { REJECT_REASON_LIST } from '../constants/reasons';

const ResubmitForm = ({ handleClose }) => {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { clearAllModal, openModal } = useModalContext();
  const { resubmitData, dispatchResubmitData } = useFormStep();
  const {
    checkId,
    validFrontImage,
    validBackImage,
    validAmount,
    reason,
    status,
    frontImageErrorMessage,
    backImageErrorMessage,
    amountErrorMessage,
    amount,
    idempotencyKey
  } = resubmitData;
  const { accessToken } = useAuthentication();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { remains, error: errorLimit, limit } = useCheckDepositLimits();
  const { dailyCheckDepositRemain, monthlyCheckDepositRemain } = remains || {};
  const { daily, monthly } = limit || {};
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { createCommonErrorToast, createErrorToast } = useToastContext();

  const {
    setCheckFrontImageResult,
    setCheckBackImageResult,
    isLoadingFrontImage,
    isLoadingBackImage,
    warningFrontImage,
    warningBackImage,
    checkCameraPermission
  } = useMitekSdkContext();

  const [moneyInput, setMoneyInput] = useState({
    value: amount,
    isValid: validAmount,
    isError: false,
    message: ''
  });
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [loadingFrontImage, setLoadingFrontImage] = useState(false);
  const [loadingBackImage, setLoadingBackImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpeningModal } = useCheckOpenModal();
  const captureException = useCaptureException();

  const isDisableSubmit = Boolean(
    !moneyInput.isValid ||
      !frontImage ||
      !backImage ||
      isSubmitting ||
      warningFrontImage ||
      warningBackImage
  );

  const handleResubmitImagesStatusError = (error) => {
    const title = error?.response?.data?.title;
    const description = error?.response?.data?.message;
    const showReviewCTA = title !== CHECK_DEPOSIT_FAILED_ERROR_MESSAGE;

    clearAllModal();
    if (title && description) {
      createErrorToast({
        title,
        description,
        secondaryAction: showReviewCTA
          ? {
              label: 'Review',
              size: 'sm',
              onClick: () => {
                openModal({
                  component: CheckDepositHistory
                });
              }
            }
          : {},

        error
      });
    } else {
      createCommonErrorToast({ error });
    }
  };

  const resubmitImagesStatus = async () => {
    setIsSubmitting(true);
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      setIsSubmitting(false);
      return;
    }

    const form = await createFormData({
      frontImage,
      backImage,
      amount,
      checkId
    });

    const path = generatePath(
      `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/submit`,
      {
        businessEntityUuid,
        unitAccountId
      }
    );
    try {
      const { data, status: statusCode } = await axios.post(path, form, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
          'Customer-Token': customerToken,
          'Idempotency-Key': idempotencyKey
        }
      });

      dispatchResubmitData({
        type: RESUBMIT_ACTIONS.START_LOADING
      });
      // submit success
      if (statusCode === 200) {
        const unitStatus = data?.attributes?.status;
        const unitReason = data?.attributes?.reason;

        const errorStatus = [
          CHECK_DEPOSIT_UNIT_STATUS.REJECTED,
          CHECK_DEPOSIT_UNIT_STATUS.AWAITING_CUSTOMER_CONFIRMATION
        ];

        if (errorStatus.includes(unitStatus)) {
          dispatchResubmitData({
            type: RESUBMIT_ACTIONS.UPDATE_UNIT_STATUS_AND_REASON,
            payload: {
              unitStatus,
              unitReason
            }
          });
          return;
        }

        dispatchResubmitData({
          type: RESUBMIT_ACTIONS.GO_TO_OVERVIEW,
          payload: {
            amount: moneyInput.value
          }
        });

        if (!isOpeningModal) {
          dispatchRefetchCheckDeposit();
        }
      } else {
        throw new Error(`Resubmit check's images failed with statusCode: ${statusCode}`);
      }
    } catch (error) {
      handleResubmitImagesStatusError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const createFormDataByReason = async (confirmationReason) => {
    switch (confirmationReason) {
      case REJECT_REASON_LIST.UPDATE_CHECK_AMOUNT: {
        const form = await createFormData({ amount });
        return form;
      }
      case REJECT_REASON_LIST.UPLOAD_FRONT_SIDE_IMAGE: {
        const form = await createFormData({ frontImage });
        return form;
      }
      case REJECT_REASON_LIST.UPLOAD_BACK_SIDE_IMAGE:
      case REJECT_REASON_LIST.UPLOAD_BACK_SIDE_IMAGE_ONLY_MOBILE: {
        const form = await createFormData({ backImage });
        return form;
      }
      default:
        return null;
    }
  };

  const resubmitUserConfirmationStatus = async () => {
    setIsSubmitting(true);
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      setIsSubmitting(false);
      return;
    }

    const path = generatePath(
      `${DEFAULT_SEGMENT}/check-deposits/confirm/:businessEntityUuid/:unitAccountId/:checkId`,
      {
        businessEntityUuid,
        unitAccountId,
        checkId
      }
    );

    const form = await createFormDataByReason(reason);
    try {
      const { status: statusCode } = await axios.post(path, form, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
          'Customer-Token': customerToken
        }
      });

      // submit success
      if (statusCode === 200) {
        dispatchResubmitData({
          type: RESUBMIT_ACTIONS.START_LOADING
        });

        dispatchResubmitData({
          type: RESUBMIT_ACTIONS.GO_TO_OVERVIEW,
          payload: {
            amount: moneyInput.value
          }
        });

        if (!isOpeningModal) {
          dispatchRefetchCheckDeposit();
        }
      } else {
        throw new Error(`Resubmit user confirmation failed with statusCode: ${statusCode}`);
      }
    } catch (error) {
      handleResubmitImagesStatusError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResubmit = async () => {
    switch (status) {
      case CHECK_DEPOSIT_STATUS.AWAITING_IMAGES:
      case CHECK_DEPOSIT_STATUS.AWAITING_BACK_IMAGE:
      case CHECK_DEPOSIT_STATUS.AWAITING_FRONT_IMAGE:
        await resubmitImagesStatus();
        break;
      case CHECK_DEPOSIT_STATUS.AWAITING_CUSTOMER_CONFIRMATION:
        await resubmitUserConfirmationStatus();
        break;
      default:
        break;
    }
  };

  const saveCapturedImageHandler = useCallback(
    async (imageData, docType) => {
      if (!imageData || !docType) {
        return;
      }
      try {
        const rotatedImage = await checkImageDataToRotate(imageData);
        if (docType === CHECK_FRONT_DOC_TYPE) {
          setFrontImage(rotatedImage);
        } else {
          setBackImage(rotatedImage);
        }
      } catch (error) {
        captureException(error);
      }
    },
    [captureException]
  );

  useEffect(() => {
    const downloadAndSetImage = async (setImage, setLoading, endpoint) => {
      try {
        setLoading(true);
        const customerToken = await getCustomerToken();

        if (!customerToken) {
          handleClose();
          return;
        }

        await fetchAndSaveImage({
          setImage,
          endpoint,
          accessToken,
          customerToken
        });
      } catch (error) {
        handleClose();
        createCommonErrorToast({ error });
      } finally {
        setLoading(false);
      }
    };

    const setFrontImageMitek = (image) => {
      setFrontImage(image);
      setCheckFrontImageResult(image);
    };

    const setBackImageMitek = (image) => {
      setBackImage(image);
      setCheckBackImageResult(image);
    };

    const frontImageEndpoint = generatePath(
      `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/:checkId/front`,
      { businessEntityUuid, unitAccountId, checkId }
    );
    const backImageEndpoint = generatePath(
      `${DEFAULT_SEGMENT}/check-deposits/:businessEntityUuid/:unitAccountId/:checkId/back`,
      { businessEntityUuid, unitAccountId, checkId }
    );

    if (validFrontImage) {
      downloadAndSetImage(setFrontImageMitek, setLoadingFrontImage, frontImageEndpoint);
    }

    if (validBackImage) {
      downloadAndSetImage(setBackImageMitek, setLoadingBackImage, backImageEndpoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorLimit) {
      handleClose();
      createCommonErrorToast({ error: errorLimit });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorLimit]);

  const invalidValues = useMemo(
    () => (validAmount ? [] : [{ value: amount, message: amountErrorMessage }]),
    [amount, amountErrorMessage, validAmount]
  );

  useEffect(() => {
    if (!validFrontImage || !validBackImage) {
      checkCameraPermission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validFrontImage, validBackImage]);

  return (
    <Stack
      height="100%"
      mx="16px"
    >
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper>
              <Box padding={isDesktop ? '80px 0px 40px 0' : '32px 24px'}>
                <DepositMoneyInput
                  constants={{
                    dailyLimit: daily,
                    monthlyLimit: monthly
                  }}
                  defaultValue={amount}
                  handleDataChange={setMoneyInput}
                  invalidValues={invalidValues}
                  isDisable={isSubmitting || validAmount}
                  remainLimits={{
                    dailyRemainLimit: dailyCheckDepositRemain,
                    monthlyRemainLimit: monthlyCheckDepositRemain
                  }}
                />
              </Box>
              <SectionWrapperStyled>
                <DepositTo title="Deposit to" />
                <DividerStyled />
                <CapturedImage
                  errorMessage={frontImageErrorMessage}
                  imageData={frontImage}
                  isDisable={isSubmitting || validFrontImage || isLoadingBackImage}
                  loading={loadingFrontImage || isLoadingFrontImage}
                  saveCapturedImageHandler={saveCapturedImageHandler}
                  sideType={CAPTURE_FRONT_SIDE}
                  warningMessage={warningFrontImage}
                />
                <DividerStyled />
                <CapturedImage
                  errorMessage={backImageErrorMessage}
                  imageData={backImage}
                  isDisable={isSubmitting || validBackImage || isLoadingFrontImage}
                  loading={loadingBackImage || isLoadingBackImage}
                  saveCapturedImageHandler={saveCapturedImageHandler}
                  sideType={CAPTURE_BACK_SIDE}
                  warningMessage={warningBackImage}
                />
                <DividerStyled />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                >
      
                  <Text
                    color="primary"
                    variant={isDesktop ? 'lg' : 'md'}
                  >
                    Auto capture
                  </Text>
                  <Switch
                    checked
                    textColor="primary"
                  />
                </Stack>
              </SectionWrapperStyled>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid>
        <LoadingButton
          aria-label="submit"
          color="primary"
          disabled={isDisableSubmit || isLoadingFrontImage || isLoadingBackImage}
          fullWidth
          loading={isSubmitting}
          onClick={handleResubmit}
          size="large"
          variant="contained"
        >
          Submit
        </LoadingButton>
      </ActionButtonsGrid>
    </Stack>
  );
};

ResubmitForm.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default memo(ResubmitForm);
